import type { Site } from '~/interfaces/sites/main';
import type { JsonApiResponse } from '~/interfaces/api/main';
import { useAuthFetchNext } from '~/composables/useAuthFetch.next';
import { isAPIErrorResponse } from '~/composables/useUtils';
import { createApiError, unwrapApiItem } from '~/api/utils';

export interface SitesListResult {
  items: Site[];
  totalItems: number;
}

export interface SiteDetailResult {
  item: Site | null;
  logoPath: string | null;
  logoExtension: string | null;
}

export const getSitesList = async (
  params?: string | null,
): Promise<SitesListResult> => {
  const urlParams = params ? '?' + params : '';
  const response = await useAuthFetchNext(`sites${urlParams}`);

  if (isAPIErrorResponse(response)) {
    throw createApiError('Impossible de récupérer les sites', response.errors);
  }

  const payload = response as JsonApiResponse<Site[]>;

  return {
    items: payload.data ?? [],
    totalItems: payload.meta?.totalItems ?? 0,
  };
};

export const getSiteDetail = async (
  id: number | string,
): Promise<SiteDetailResult> => {
  const response = await useAuthFetchNext(`sites/${id}`);

  if (isAPIErrorResponse(response)) {
    throw createApiError('Impossible de récupérer le site', response.errors);
  }

  const data = unwrapApiItem<Site>(response);

  return {
    item: data ?? null,
    logoPath: data?.logoPath ?? null,
    logoExtension: data?.logoExtension ?? null,
  };
};

export const getAllSites = async (): Promise<Site[]> => {
  const response = await useAuthFetchNext('sites');

  if (isAPIErrorResponse(response)) {
    throw createApiError('Impossible de récupérer les sites', response.errors);
  }

  const payload = response as JsonApiResponse<Site[]>;
  return payload.data ?? [];
};

export const deleteSitesBatch = async (
  sitesIds: number[],
): Promise<void> => {
  const response = await useAuthFetchNext('sites', {
    method: 'DELETE',
    body: { ids: sitesIds },
  });

  if (isAPIErrorResponse(response)) {
    throw createApiError('Le site n\'a pas pu être supprimé', response.errors);
  }
};
